import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BookOpen, Heart, MessageCircle, Users, CheckCircle, Shield, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const pillars = [
  { icon: BookOpen, title: "Articles & Guides", desc: "Career growth, maternity return, leadership, salary negotiation \u2014 curated for women professionals" },
  { icon: Heart, title: "Real Stories", desc: "Hear from women who navigated challenges, broke barriers, and kept growing" },
  { icon: MessageCircle, title: "Safe Space Forum", desc: "Discuss, ask, share \u2014 post anonymously if you prefer. Zero judgment." },
  { icon: Users, title: "1-on-1 Mentoring", desc: "Connect with senior women leaders who have walked this path before you" },
];

const articles = [
  { title: "Returning After Maternity Leave \u2014 A 30-Day Reintegration Plan", category: "Maternity & Career", author: "RoleIQ Team", time: "7 min read", slug: "returning-to-work-after-maternity-leave-30-day-playbook" },
  { title: "How to Negotiate Your Salary Without Feeling Uncomfortable", category: "Salary & Finance", author: "RoleIQ Team", time: "5 min read", slug: "how-to-negotiate-your-salary-without-feeling-uncomfortable" },
  { title: "Managing Unconscious Bias in the Workplace \u2014 A Practical Guide", category: "Managing Bias", author: "RoleIQ Team", time: "5 min read", slug: "managing-unconscious-bias-in-the-workplace" },
  { title: "Building Your Leadership Brand as a Woman in a Male-Dominated Industry", category: "Leadership", author: "RoleIQ Team", time: "5 min read", slug: "building-your-leadership-brand-as-a-woman" },
  { title: "Work-Life Integration \u2014 Why Balance is a Myth and What Actually Works", category: "Work-Life Integration", author: "RoleIQ Team", time: "5 min read", slug: "work-life-integration-why-balance-is-a-myth" },
  { title: "Maternity Leave and Career Continuity \u2014 What Your Employer Owes You", category: "Maternity & Career", author: "RoleIQ Team", time: "5 min read", slug: "maternity-leave-and-career-continuity" },
];

const forumPosts = [
  { title: "How did you handle the guilt of going back to work after baby?", author: "Anonymous", time: "3 hours ago", replies: 14, likes: 28, topic: "Maternity leave return" },
  { title: "Tips for negotiating a raise when you feel undervalued", author: "Ritu S.", time: "6 hours ago", replies: 22, likes: 45, topic: "Negotiating salary" },
  { title: "How do I deal with being the only woman in leadership meetings?", author: "Anonymous", time: "1 day ago", replies: 18, likes: 36, topic: "Dealing with bias" },
  { title: "Rebuilding confidence after a 3-year career break", author: "Kavita M.", time: "1 day ago", replies: 11, likes: 20, topic: "Building confidence" },
  { title: "Moving from HR to product management \u2014 has anyone done this?", author: "Priya N.", time: "2 days ago", replies: 8, likes: 15, topic: "Career pivots" },
  { title: "Resources for women re-entering the workforce after 40", author: "Anonymous", time: "3 days ago", replies: 16, likes: 32, topic: "Re-entering workforce" },
];

const sheMentors = [
  { name: "Anjali Sharma", role: "VP HR", company: "Automotive", expertise: ["Career transitions", "Maternity return"] },
  { name: "Pooja Mehta", role: "L&D Director", company: "Banking", expertise: ["Leadership for women", "Salary negotiation"] },
  { name: "Ritu Singh", role: "CHRO", company: "IT", expertise: ["Breaking barriers", "Executive presence"] },
  { name: "Kavita Nair", role: "HR Consultant", company: "Healthcare", expertise: ["Work-life integration", "Confidence building"] },
];

const quotes = [
  { text: "The question isn\u2019t who\u2019s going to let me; it\u2019s who\u2019s going to stop me.", author: "Ayn Rand" },
  { text: "I have learned over the years that when one\u2019s mind is made up, this diminishes fear.", author: "Rosa Parks" },
  { text: "Above all, be the heroine of your life, not the victim.", author: "Nora Ephron" },
  { text: "We need women at all levels, including the top, to change the dynamic, reshape the conversation.", author: "Sheryl Sandberg" },
  { text: "A woman with a voice is, by definition, a strong woman.", author: "Melinda Gates" },
];

function AccessGate({ user, onRequestSubmitted }) {
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleRequest = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/sheleads/request-access`, { linkedin_url: linkedinUrl }, { withCredentials: true });
      setSubmitted(true);
      if (onRequestSubmitted) onRequestSubmitted();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #C9A84C, #8B6914)" }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-10 text-center">
          <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-gold" />
          </div>
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">She Leads is a Safe Space</h1>
          <p className="font-dm text-navy/60 text-sm mb-6 leading-relaxed">
            She Leads is a safe space created <strong>exclusively for women professionals</strong>. To keep this community secure and supportive, we verify access before granting entry. Thank you for your understanding and cooperation.
          </p>
          <a href="/signup" className="inline-block bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors" data-testid="sheleads-signup-btn">
            Create Account to Request Access
          </a>
          <p className="font-dm text-navy/40 text-xs mt-4">Already a member? <a href="/login" className="text-gold hover:underline">Login</a></p>
        </div>
      </div>
    );
  }

  // Gender not female
  if (user.gender?.toLowerCase() !== "female") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #C9A84C, #8B6914)" }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-10 text-center">
          <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-gold" />
          </div>
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">Women-Only Space</h1>
          <p className="font-dm text-navy/60 text-sm mb-6 leading-relaxed">
            She Leads is a dedicated, safe space <strong>exclusively for women professionals</strong>. To access this section, your profile must indicate <strong>Female</strong> as your gender. Please update your profile in <a href="/settings" className="text-gold hover:underline font-semibold">Account Settings</a> if applicable.
          </p>
          <p className="font-dm text-navy/40 text-xs">Thank you for your understanding.</p>
        </div>
      </div>
    );
  }

  // Pending access
  if (user.sheleads_access === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #C9A84C, #8B6914)" }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-10 text-center">
          <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-gold" />
          </div>
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">Request Under Review</h1>
          <p className="font-dm text-navy/60 text-sm mb-4 leading-relaxed">
            Your request to access She Leads is currently being reviewed by our team. We'll verify your profile and grant access soon. Thank you for your patience.
          </p>
          <p className="font-dm text-navy/40 text-xs">This usually takes less than 24 hours.</p>
        </div>
      </div>
    );
  }

  // Rejected
  if (user.sheleads_access === "rejected") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #C9A84C, #8B6914)" }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-10 text-center">
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">Access Not Granted</h1>
          <p className="font-dm text-navy/60 text-sm mb-4 leading-relaxed">
            Unfortunately, your request was not approved. If you believe this was an error, please contact us at <a href="/contact" className="text-gold hover:underline">our contact page</a>.
          </p>
        </div>
      </div>
    );
  }

  // Request access form (female, not yet requested)
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #C9A84C, #8B6914)" }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-10 text-center">
          <CheckCircle className="w-12 h-12 text-gold mx-auto mb-4" />
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">Request Submitted!</h1>
          <p className="font-dm text-navy/60 text-sm">Your request is under review. We'll notify you once approved.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #C9A84C, #8B6914)" }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-10">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-gold" />
          </div>
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">Request She Leads Access</h1>
          <p className="font-dm text-navy/60 text-sm leading-relaxed">
            She Leads is a safe space <strong>exclusively for women professionals</strong>. To maintain safety and trust, we verify each member before granting access. Thank you for your cooperation.
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="font-dm text-navy text-sm font-semibold block mb-1">LinkedIn Profile URL (for verification)</label>
            <input value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://linkedin.com/in/your-profile" data-testid="sheleads-linkedin-input"
              className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
            <p className="font-dm text-navy/40 text-xs mt-1">Helps us verify your professional identity</p>
          </div>
          {error && <p className="font-dm text-red-500 text-xs">{error}</p>}
          <button onClick={handleRequest} disabled={loading} data-testid="sheleads-request-btn"
            className="w-full bg-gold text-navy font-dm font-bold py-3 rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50">
            {loading ? "Submitting..." : "Request Access"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SheLeadsPage() {
  const [quoteIdx, setQuoteIdx] = useState(0);
  const { user, loading } = useAuth();
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessData, setAccessData] = useState(null);
  const [exploreError, setExploreError] = useState("");

  useEffect(() => {
    const timer = setInterval(() => setQuoteIdx((p) => (p + 1) % quotes.length), 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Wait for auth loading to complete before checking access
    if (loading) return;
    
    if (user) {
      checkAccess();
    } else {
      setAccessChecked(true);
    }
  }, [user, loading]);

  async function checkAccess() {
    try {
      const res = await axios.get(`${API}/api/sheleads/check-access`, { withCredentials: true });
      setAccessData(res.data);
    } catch {
      setAccessData({ access: "", gender: "" });
    } finally {
      setAccessChecked(true);
    }
  }

  function handleExploreClick() {
    setExploreError("");
    if (!user) {
      // Redirect to login with message in sessionStorage
      sessionStorage.setItem("login_message", "Please log in to access She Leads community.");
      window.location.href = "/login";
      return;
    }
    const userGender = (accessData?.gender || user.gender || "").toLowerCase();
    const isAdmin = user.role === "admin";
    if (!isAdmin && userGender !== "female") {
      setExploreError("She Leads is exclusively for women professionals. This is verified at registration and cannot be changed. Please contact hello@roleiq.pro if you believe this is an error.");
      // Auto-clear after 8s
      setTimeout(() => setExploreError(""), 12000);
      return;
    }
    // Female (or admin): scroll to community content
    const target = document.getElementById("she-leads-community");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  if (loading || !accessChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-cream"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  }

  const userWithAccess = user ? { ...user, gender: accessData?.gender || user.gender, sheleads_access: accessData?.access || user.sheleads_access } : null;
  const isAdmin = user?.role === "admin";
  const hasAccess = isAdmin || (userWithAccess && userWithAccess.gender?.toLowerCase() === "female");
  const needsGenderUpdate = user && (!userWithAccess?.gender || userWithAccess.gender === "");

  // Everyone sees the landing page. Restricted content is gated inline.
  return (
    <div>
      {/* Hero - PUBLIC */}
      <section className="min-h-[80vh] flex items-center justify-center" style={{ background: "linear-gradient(135deg, #C9A84C, #8B6914)" }}>
        <div className="max-w-4xl mx-auto px-4 text-center py-20">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-block bg-white/20 text-white font-dm text-sm px-4 py-1.5 rounded-full mb-6">She Leads by RoleIQ</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-playfair text-white text-3xl sm:text-4xl lg:text-[56px] font-bold mb-6 leading-tight">She Leads. She Grows. She Inspires.</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="font-dm text-white/80 text-base max-w-xl mx-auto mb-8">She Leads was born from a simple observation — that talented women across every industry, every role, and every life stage often lack the structured support to keep growing. Whether you are returning after maternity leave, navigating a career pivot, pushing for a promotion, or simply looking for someone who understands your journey — this is your space.</motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            <button onClick={handleExploreClick} data-testid="sheleads-explore-btn" className="inline-block bg-white text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors">Explore She Leads</button>
          </motion.div>
          <p className="font-dm text-white/50 text-sm mt-8">Open to all working women &middot; All industries &middot; All career stages &middot; Safe and moderated</p>
          {exploreError && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} data-testid="sheleads-explore-error"
              className="max-w-2xl mx-auto mt-6 bg-white/95 backdrop-blur-sm border-l-4 border-red-500 rounded-lg shadow-lg p-4 text-left">
              <p className="font-dm text-navy text-sm leading-relaxed">{exploreError}</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Pillars - PUBLIC */}
      <section className="bg-cream py-20">
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-12">Everything you need to keep growing</motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, i) => (
              <motion.div key={p.title || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white border-t-4 border-gold rounded-xl shadow-md p-6 hover:-translate-y-1 transition-transform">
                <p.icon className="w-8 h-8 text-gold mb-3" />
                <h3 className="font-dm text-navy font-semibold mb-2">{p.title}</h3>
                <p className="font-dm text-navy/60 text-sm">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RESTRICTED CONTENT - Forum, Stories, Mentoring */}
      {!hasAccess ? (
        <section className="bg-white py-20">
          <div className="max-w-lg mx-auto px-4 text-center">
            <Shield className="w-12 h-12 text-gold mx-auto mb-4" />
            <h2 className="font-playfair text-navy text-2xl font-bold mb-3">Community Content</h2>
            {!user ? (
              <><p className="font-dm text-navy/60 text-sm mb-6">This space is exclusively for women professionals. Create an account to request access.</p>
              <a href="/signup" className="inline-block bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg">Create Account</a></>
            ) : needsGenderUpdate ? (
              <><p className="font-dm text-navy/60 text-sm mb-6">This space is exclusively for women professionals. Please update your gender in Account Settings to access She Leads community features.</p>
              <a href="/settings" className="inline-block bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg">Update Profile Settings</a></>
            ) : (
              <><p className="font-dm text-navy/60 text-sm mb-6">She Leads community features are exclusively for women professionals.</p></>
            )}
          </div>
        </section>
      ) : (
        <>

      {/* Articles */}
      <section id="she-leads-community" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-12">
            Resources for every stage of your journey
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {articles.map((a, i) => (
              <motion.div key={a.title || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl shadow-md hover:shadow-lg hover:-translate-y-1 p-6 transition-all">
                <span className="inline-block bg-gold/10 text-gold font-dm text-xs px-3 py-1 rounded-full mb-3">{a.category}</span>
                <h3 className="font-playfair text-navy font-semibold mb-2">{a.title}</h3>
                <p className="font-dm text-navy/50 text-xs mb-4">{a.author} &middot; {a.time}</p>
                <Link to={`/blog/${a.slug}`} className="border border-gold text-gold font-dm text-sm px-4 py-1.5 rounded-lg hover:bg-gold/10 transition-colors inline-block">Read More</Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Forum */}
      <section className="bg-cream py-20">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-3xl text-center font-semibold mb-8">A safe space to speak freely</h2>
          <div className="space-y-4 mb-8">
            {forumPosts.map((p, i) => (
              <div key={p.title || i} className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md p-5 transition-shadow">
                <span className="inline-block bg-gold/10 text-gold font-dm text-xs px-3 py-1 rounded-full mb-2">{p.topic}</span>
                <h3 className="font-dm text-navy font-semibold mb-1">{p.title}</h3>
                <div className="flex items-center gap-4 text-navy/50 font-dm text-xs">
                  <span>{p.author}</span><span>{p.time}</span>
                  <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {p.replies}</span>
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {p.likes}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mentors */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-3xl text-center font-semibold mb-12">Connect with women who have walked this path before you</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {sheMentors.map((m, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md hover:shadow-lg p-6 text-center border border-gold/10 transition-shadow">
                <div className="w-16 h-16 bg-gold/10 rounded-full border-2 border-gold mx-auto mb-3" />
                <h4 className="font-dm text-navy font-semibold">{m.name}</h4>
                <p className="font-dm text-navy/50 text-xs mb-2">{m.role}, {m.company}</p>
                <div className="flex flex-wrap justify-center gap-1 mb-4">
                  {m.expertise.map((e) => <span key={e} className="bg-navy/5 text-navy/60 font-dm text-[10px] px-2 py-0.5 rounded-full">{e}</span>)}
                </div>
                <button className="border border-gold text-gold font-dm text-sm px-4 py-1.5 rounded-lg hover:bg-gold/10 transition-colors">Connect</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quotes */}
      <section className="bg-navy py-20">
        <div className="max-w-3xl mx-auto px-4 text-center min-h-[160px] flex flex-col items-center justify-center">
          <span className="font-playfair text-gold text-6xl">&ldquo;</span>
          <motion.p key={quoteIdx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
            className="font-playfair text-white text-xl sm:text-2xl italic leading-relaxed -mt-4 mb-4"
          >
            {quotes[quoteIdx].text}
          </motion.p>
          <p className="font-dm text-gold text-sm">\u2014 {quotes[quoteIdx].author}</p>
          <div className="flex gap-2 mt-6">
            {quotes.map((_, i) => (
              <button key={i} onClick={() => setQuoteIdx(i)} className={`w-2.5 h-2.5 rounded-full transition-colors ${i === quoteIdx ? "bg-gold" : "bg-white/20"}`} />
            ))}
          </div>
        </div>
      </section>

      </>
      )}

      {/* Newsletter */}
      <section className="bg-gold py-16">
        <div className="max-w-xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-navy text-2xl font-bold mb-4">Get weekly inspiration, resources, and opportunities</h2>
          <SheLeadsNewsletter />
          <p className="font-dm text-navy/50 text-xs mt-3">Join 500+ women professionals. Unsubscribe anytime.</p>
        </div>
      </section>
    </div>
  );
}

function SheLeadsNewsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!email.trim() || !email.includes("@")) {
      setStatus("error");
      setMessage("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/newsletter/subscribe`, { email: email.trim() });
      setStatus("success");
      setMessage(res.data.message);
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || "Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  return status === "success" ? (
    <div className="flex items-center justify-center gap-2">
      <CheckCircle className="w-5 h-5 text-navy" />
      <span className="font-dm text-navy text-sm">{message}</span>
    </div>
  ) : (
    <div>
      <div className="flex gap-2">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" data-testid="sheleads-newsletter-email"
          className="flex-1 px-4 py-3 rounded-lg font-dm text-sm focus:outline-none" />
        <button onClick={handleSubscribe} disabled={loading} data-testid="sheleads-newsletter-submit"
          className="bg-navy text-white font-dm font-bold px-6 py-3 rounded-lg hover:bg-navy/90 transition-colors disabled:opacity-50">
          {loading ? "..." : "Subscribe"}
        </button>
      </div>
      {status === "error" && <p className="font-dm text-red-600 text-xs mt-2">{message}</p>}
    </div>
  );
}
